import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { listCatalogFormOptionsApi } from 'api/drug';
import { updateCatalogDrug } from 'store/thunks/catalogThunks';

import CatalogDialogHeader from './CatalogDialogHeader';
import { formatDrugName, formatStrength } from '../utils/format';

// e-scribe-ui's shared TextField wrapper always renders a helper-text line --
// real text, or a single space as a fallback -- so every field, edited or
// read-only, reserves the same bottom space. A plain MUI TextField with no
// helperText reserves none, which is what left Compounding Drug (a hand-built
// box with its own invisible placeholder) out of step with its neighbours.
// Passing this same fallback keeps every field's box the same height.
const RESERVED_HELPER_TEXT = ' ';

// Matches the invisible FormHelperText's own sizing below, so a TextField's
// real (or reserved-space) helper text takes up exactly as much room as the
// Compounding Drug box's placeholder does.
const HELPER_TEXT_PROPS = { sx: { minHeight: '1.6em', mb: 1 } };

// Mirrors e-scribe-ui's EditFormularyDialog: only Pack Size Value/Unit and
// Compounding Drug are actually editable here. Drug Name, Product Id,
// Strength, Drug Code and Form are shown for context but are read-only --
// changing any of those is a new catalog identity, not an edit of this one.
const EditCatalogDialog = ({ open, item, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [packSizeValue, setPackSizeValue] = useState('');
  const [packSizeUnit, setPackSizeUnit] = useState('');
  const [compounding, setCompounding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  useEffect(() => {
    if (!open || !item) return;

    setPackSizeValue(item.packSizeValue ?? '');
    setPackSizeUnit(item.packSizeUnit ?? '');
    setCompounding(!!item.isCompoundingEligible);
    setLoadingForms(true);

    listCatalogFormOptionsApi()
      .then(setFormOptions)
      .finally(() => setLoadingForms(false));
  }, [open, item]);

  const unitsForCurrentForm = useMemo(() => {
    const selected = formOptions.find(({ formAlias: alias }) => alias === item?.formAlias);

    return [...(selected?.units ?? [])].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    );
  }, [formOptions, item?.formAlias]);

  const handleSave = () => {
    setSaving(true);

    dispatch(
      updateCatalogDrug({
        catalogId: item.catalogId,
        packSizeValue: packSizeValue.trim() || null,
        packSizeUnit: packSizeUnit || null,
        isCompoundingEligible: compounding,
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar('Catalog Updated Successfully', { variant: 'success' });
          onClose();
        }
      })
      .finally(() => setSaving(false));
  };

  if (!item) {
    return null;
  }

  // Only Cancel and the header's X close this -- a click on the backdrop
  // shouldn't discard an in-progress edit.
  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='md' fullWidth>
      <CatalogDialogHeader
        title={`Edit Catalog - ${formatDrugName(item.drugName)}`}
        onClose={handleDialogClose}
      />

      <DialogContent>
        <TextField
          id='EditCatalogDialog-drugName-input'
          label='Drug Name'
          value={formatDrugName(item.drugName)}
          disabled
          fullWidth
          sx={{ mt: 1 }}
          helperText={RESERVED_HELPER_TEXT}
          FormHelperTextProps={HELPER_TEXT_PROPS}
        />

        <TextField
          id='EditCatalogDialog-productId-input'
          label='Product ID'
          value={item.productId ?? ''}
          disabled
          fullWidth
          sx={{ mt: 1 }}
          helperText='Automatically generated from Drug Code, Strength, and Form'
          FormHelperTextProps={HELPER_TEXT_PROPS}
        />

        {/* Plain flex rows with `flex: 1` on every child, rather than Grid: Grid's
            columnSpacing negative-margin math left later columns of a row narrower
            than the first instead of a clean equal split. */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            id='EditCatalogDialog-strength-input'
            label='Strength'
            value={formatStrength(item.strength)}
            disabled
            sx={{ flex: 1, mt: 1 }}
            helperText={RESERVED_HELPER_TEXT}
            FormHelperTextProps={HELPER_TEXT_PROPS}
          />
          <TextField
            id='EditCatalogDialog-drugCode-input'
            label='Drug Code'
            value={item.drugCode ?? ''}
            disabled
            sx={{ flex: 1, mt: 1 }}
            helperText={RESERVED_HELPER_TEXT}
            FormHelperTextProps={HELPER_TEXT_PROPS}
          />
          <FormControl variant='outlined' sx={{ flex: 1, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={compounding}
                  onChange={(event) => setCompounding(event.target.checked)}
                />
              }
              label='Compounding Drug'
              sx={{
                ml: 0,
                border: '1px solid',
                borderColor: compounding ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: 2,
                height: '56px',
                width: '100%',
              }}
            />
            <FormHelperText sx={{ minHeight: '1.6em', mb: 1, visibility: 'hidden' }}>
              placeholder
            </FormHelperText>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            id='EditCatalogDialog-packSizeValue-input'
            label='Pack Size Value'
            type='number'
            value={packSizeValue}
            onChange={(event) => setPackSizeValue(event.target.value)}
            sx={{ flex: 1 }}
            helperText={RESERVED_HELPER_TEXT}
            FormHelperTextProps={HELPER_TEXT_PROPS}
          />
          <TextField
            id='EditCatalogDialog-packSizeUnit-input'
            select
            label='Pack Size Unit'
            value={packSizeUnit}
            onChange={(event) => setPackSizeUnit(event.target.value)}
            disabled={loadingForms || unitsForCurrentForm.length === 0}
            sx={{ flex: 1 }}
            helperText={RESERVED_HELPER_TEXT}
            FormHelperTextProps={HELPER_TEXT_PROPS}
          >
            {unitsForCurrentForm.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id='EditCatalogDialog-formAlias-input'
            label='Form'
            value={item.formAlias ?? ''}
            disabled
            sx={{ flex: 1 }}
            helperText={RESERVED_HELPER_TEXT}
            FormHelperTextProps={HELPER_TEXT_PROPS}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton loading={saving} variant='contained' onClick={handleSave} disabled={saving}>
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EditCatalogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    catalogId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    productId: PropTypes.string,
    drugCode: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    isCompoundingEligible: PropTypes.bool,
  }),
};

EditCatalogDialog.defaultProps = {
  item: null,
};

export default EditCatalogDialog;
